import React, { useEffect, useState, useCallback, useRef } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import LoginPopup from "../../components/general/LoginPopup";
import { UserProvider } from "../auth/UserContext";
import Pages from "../../components/Pages";
import {
    initBroadcastChannel,
    handleBroadcastMessage,
    closeBroadcastChannel,
} from "../sessionHandling/BroadcastChannelsUtils";
import { resetInactivityTimer, initInactivityTimer } from "../sessionHandling/InactivityUtils";

const AuthWrapper = () => {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [logoutMessage, setLogoutMessage] = useState(null);
    const [isInitializing, setIsInitializing] = useState(true);
    const isAuthenticated = useIsAuthenticated();
    const { instance, inProgress } = useMsal();
    const logoutChannel = useRef(initBroadcastChannel("logout_channel"));

    const clearAuthState = () => {
        setIsLoggedIn(false);
        setLogoutMessage("You have been logged out due to inactivity.");
        localStorage.removeItem("isLoggedIn");
    };

    const handleLogout = useCallback(() => {
        if (inProgress !== "none") {
            console.warn("Another interaction is already in progress. Logout skipped.");
            return;
        }

        if (logoutChannel.current) {
            logoutChannel.current.postMessage("LOGOUT");
        }

        const activeAccount = instance.getActiveAccount();
        clearAuthState();

        if (activeAccount) {
            try {
                instance.logoutRedirect({
                    account: activeAccount,
                    postLogoutRedirectUri: "/",
                });
            } catch (error) {
                console.error("Error during logout:", error);
            }
        }
    }, [instance, inProgress]);

    useEffect(() => {
        handleBroadcastMessage(logoutChannel.current, clearAuthState);

        const accounts = instance.getAllAccounts();
        if (accounts.length > 0) {
            instance.setActiveAccount(accounts[0]);
        }

        const storedLoginStatus = localStorage.getItem("isLoggedIn") === "true";
        const activeAccount = instance.getActiveAccount();

        if (isAuthenticated && activeAccount) {
            instance
                .acquireTokenSilent({ scopes: ["user.read"] })
                .then(() => {
                    setIsLoggedIn(true);
                    setLogoutMessage(null);
                    initInactivityTimer(handleLogout);
                })
                .catch(() => {
                    clearAuthState();
                });
        } else if (!isAuthenticated && storedLoginStatus) {
            instance
                .ssoSilent({ scopes: ["user.read"] }) // This attempts SSO login
                .then((response) => {
                    instance.setActiveAccount(response.account);
                    setIsLoggedIn(true);
                    setLogoutMessage(null);
                    localStorage.setItem("isLoggedIn", "true");
                    initInactivityTimer(handleLogout);
                })
                .catch(() => {
                    clearAuthState();
                });
        } else {
            clearAuthState();
        }

        setIsInitializing(false);

        return () => closeBroadcastChannel(logoutChannel);
    }, [isAuthenticated, instance, handleLogout]);



    const handleSuccess = () => {
        const account = instance.getAllAccounts()[0];
        console.log(account);
        
        if (account) {
            instance.setActiveAccount(account);
            setIsLoggedIn(true);
            setLogoutMessage(null);
            localStorage.setItem("isLoggedIn", "true");
            if (logoutChannel.current) {
                logoutChannel.current.postMessage("LOGIN");
            }
        }
    };

    return (
        <div className="App">
            {isLoggedIn ? (
                <UserProvider>
                    <Pages />
                </UserProvider>
            ) : (
                <LoginPopup onSuccess={handleSuccess} message={!isInitializing ? logoutMessage : null} />
            )}
        </div>
    );
};

export default AuthWrapper;
